/**
 * @file Handle discover-album-folder redirect requests.
 */

import React, { Component } from 'react';
import { compose } from 'redux';
import { Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import {
  onSelectIdentity as onSelectIdentityAction,
  setIsPlicCheckoutFlag,
} from '../../actions/discover-album-folder';
import { MeWithIdentities } from '../../graphql/queries/account';
import { areValidIdentities } from '../../util/account';
import { sentryCaptureMessage } from '../../util/sentry';
import { AlbumFolderWithCreatorBrandDetails } from '../../graphql/queries/album-folder';
import FullScreenLoader from '../../components/global/FullScreenLoader';
import { albumFolderIsOnboardable } from '../../util/album-folder';
import { ALBUM_TYPE } from '../../constants/albums';
import { ALL_PHOTOS_MODE } from '../../constants/album-folder';

class DiscoverAlbumFolderRedirect extends Component {
  static propTypes = {
    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
    onSelectIdentity: PropTypes.func,
    loading: PropTypes.bool,
    identities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    accountId: PropTypes.string,
    loadingAlbumFolder: PropTypes.bool,
    onSetPlicCheckout: PropTypes.func,
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
    }),
  };

  render() {
    const {
      location,
      onSelectIdentity,
      loading,
      identities,
      accountId,
      loadingAlbumFolder,
      onSetPlicCheckout,
      albumFolder,
    } = this.props;

    const {
      albumFolderId,
      identities: queryIdentities,
      skipFaces,
      plic_yearbook_checkout: isPlicCheckout,
    } = qs.parse(location.search);

    const queryIdentityArray = queryIdentities && JSON.parse(queryIdentities);
    const skipFacesBool = skipFaces && JSON.parse(skipFaces);

    if (loading || loadingAlbumFolder) {
      return <FullScreenLoader />;
    }

    if (!albumFolderId) {
      sentryCaptureMessage(
        'Folder Discovery Redirect :: Missing Album Folder',
        {
          level: 'error',
          extra: {
            accountId,
          },
        },
      );

      return <Redirect to="/not-found" />;
    }

    // At this point we have an albumFolder defined and plic too, so set plic checkout accordingly
    onSetPlicCheckout(!!isPlicCheckout);

    // Redirect to events previous to identity check, as identities are not relevant for 3 doors flow
    if (albumFolder && albumFolder.album_type === ALBUM_TYPE.EVENT) {
      return <Redirect to={`/s/event-invite/${albumFolderId}`} />;
    }

    // Check that all supplied identities are valid, else redirect to error
    if (
      queryIdentityArray &&
      queryIdentityArray.length > 0 &&
      !areValidIdentities(queryIdentityArray, identities)
    ) {
      sentryCaptureMessage('Folder Discovery Redirect :: Invalid identity', {
        level: 'error',
        extra: {
          accountId,
        },
      });

      const redirectToError = {
        pathname: '/error',
        state: {
          error: 'Link is no longer valid',
        },
      };

      return <Redirect to={redirectToError} />;
    }

    if (
      albumFolder?.joins_enabled &&
      albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC
    ) {
      return <Redirect to={`/s/all-photos-access/${albumFolderId}`} />;
    }

    if (
      albumFolder &&
      !albumFolderIsOnboardable(albumFolder) &&
      albumFolder.is_web_gallery
    ) {
      return <Redirect to={`/galleries/view/folder/${albumFolderId}`} />;
    }

    // Skip faces screen and automatically preselect this identity
    // and show them the folder's children
    if (skipFacesBool && queryIdentityArray && queryIdentityArray.length > 0) {
      const selectedIdentityId = queryIdentityArray[0];
      const selectedIdentity = identities.find(
        ({ id }) => selectedIdentityId === id,
      );

      onSelectIdentity({
        ...selectedIdentity,
        selectedAlbums: [],
        albumFolders: [
          {
            id: albumFolderId,
          },
        ],
      });

      return (
        <Redirect
          to={`/discover/album-folder/${albumFolderId}/select?${location.search}`}
        />
      );
    }

    // Skip the start screen and just go to /faces for identity selection
    if (!skipFacesBool && queryIdentityArray && queryIdentityArray.length > 0) {
      return (
        <Redirect
          to={`/discover/album-folder/${albumFolderId}/faces?${location.search}`}
        />
      );
    }

    const search = location?.search || '';
    return (
      <Redirect
        to={`/discover/album-folder/${albumFolderId}/start?${search}`}
      />
    );
  }
}

export { DiscoverAlbumFolderRedirect };

const withUserIdentities = graphql(MeWithIdentities, {
  skip: ({ authenticated }) => !authenticated,
  props: ({ data: { me, loading } }) => ({
    identities: me?.linked_identities?.records,
    accountId: me?.id,
    loading,
  }),
});

const withAlbumFolder = graphql(AlbumFolderWithCreatorBrandDetails, {
  options: ({ location }) => {
    const { albumFolderId } = qs.parse(location.search);

    return {
      variables: {
        id: albumFolderId,
      },
    };
  },
  skip: ({ location }) => {
    const { albumFolderId } = qs.parse(location.search);

    return !albumFolderId;
  },
  props: ({ data: { albumFolder, loading } }) => ({
    loadingAlbumFolder: loading,
    albumFolder,
  }),
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = dispatch => ({
  onSelectIdentity: identity => dispatch(onSelectIdentityAction(identity)),
  onSetPlicCheckout: flag => dispatch(setIsPlicCheckoutFlag(flag)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withUserIdentities,
  withAlbumFolder,
)(DiscoverAlbumFolderRedirect);
